import React, { useState, useEffect, useRef } from "react";
import {
  Navbar,
  Nav,
  Badge,
  Toggle,
  Input,
  InputGroup,
  Button,
  TagPicker,
  Modal,
  Panel,
  Dropdown,
  IconButton
} from "rsuite";
import "./_platforms.scss";
import { EPlatform, IPlatformParams } from "@bprotsyk/aso-core";
import { countryNames, getCountryLabel } from "../../../../constants/countries";
import CloseIcon from "@rsuite/icons/Close";
import initialState from "ui/admin/interface/initial-interface";
import { EDirectType, IApp } from "@bprotsyk/aso-core/lib/app/app";
import CopyIcon from "@rsuite/icons/Copy";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

interface PlatformsAccordionProps {
  app: IApp;
  platforms: Record<string, IPlatformParams | null>;
  onPlatformDataChange: (
    updatedPlatforms: Record<string, IPlatformParams>
  ) => void;
  selectedPlatform?: EPlatform | null;
  // integrationVersion removed - field no longer exists
}

type ExtendedPlatformParams = IPlatformParams & {
  newPlatform?: boolean;
};

const PlatformsAccordion: React.FC<PlatformsAccordionProps> = ({
  app,
  platforms = {},
  onPlatformDataChange,
  selectedPlatform,
  // integrationVersion removed - field no longer exists
}) => {
  const allPlatforms = Object?.values(EPlatform);
  const [active, setActive] = useState<string>(selectedPlatform || EPlatform.GENERAL);
  const [keitaroDataCompany, setKeitaroDataCompany] = useState<boolean>(true);
  const [platformStates, setPlatformStates] = useState<
    Record<string, ExtendedPlatformParams>
  >(() => {
    const initialStates: Record<string, ExtendedPlatformParams> = {};
    Object.keys(platforms || {}).forEach((key) => {
      initialStates[key] = {
        enabled: platforms[key]?.enabled || false,
        // geo field removed from IPlatformParams
        adjustParams: {
          appId: platforms[key]?.adjustParams?.appId || "",
          eventIds: {
            lead: platforms[key]?.adjustParams?.eventIds?.lead || "",
            hold: platforms[key]?.adjustParams?.eventIds?.hold || "",
            sale: platforms[key]?.adjustParams?.eventIds?.sale || "",
            rejected: platforms[key]?.adjustParams?.eventIds?.rejected || "",
          },
        },
        metricaParams: {
          appId: platforms[key]?.metricaParams?.appId || 0,
          postApiKey: platforms[key]?.metricaParams?.postApiKey || "",
        },
        // proxied field removed from IPlatformParams
        // keitaroData field removed - now only in direct.keitaroData
        appsflyerParams: platforms[key]?.appsflyerParams || {apiToken: "", devKey: "" },
        newPlatform: true,
        direct: platforms[key]?.direct || { enabled: false, keitaroData: false },
        // trackingUrl field removed from IPlatformParams
      };
    });
    return initialStates;
  });

  const [newPlatforms, setNewPlatforms] = useState<IPlatformParams[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toggleValue, setToggleValue] = useState<boolean | undefined>(
    undefined
  );

  // Custom dropdown states for direct type
  const [isDirectTypeDropdownOpen, setIsDirectTypeDropdownOpen] = useState(false);
  const [directTypeDropdownPosition, setDirectTypeDropdownPosition] = useState<{ x: number; y: number } | null>(null);
  const directTypeDropdownRef = useRef<HTMLDivElement>(null);

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    adjust: false,
    metrica: false,
    appsflyer: false
  });

  // Стан для зміни кольору іконок копіювання
  const [copyIconColors, setCopyIconColors] = useState({
    naming: '#6c757d',
    advertising_id: '#6c757d',
    appsflyer_device_id: '#6c757d'
  });

  // Стан для зміни кольору Input полів
  const [inputBorderColors, setInputBorderColors] = useState({
    naming: '#34495e',
    advertising_id: '#34495e',
    appsflyer_device_id: '#34495e'
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (directTypeDropdownRef.current && !directTypeDropdownRef.current.contains(event.target as Node)) {
        setIsDirectTypeDropdownOpen(false);
      }
    };

    if (isDirectTypeDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isDirectTypeDropdownOpen]);

  // Direct type options
  const directTypeOptions = [
    { value: EDirectType.KEITARO_OFFER, label: '🎯 Keitaro Оффер' },
    { value: EDirectType.OFFER_DIRECT, label: '🚀 Прямий Дірект' }
  ];

  const handleDirectTypeDropdownClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setDirectTypeDropdownPosition({
      x: rect.left,
      y: rect.bottom + 4
    });
    setIsDirectTypeDropdownOpen(!isDirectTypeDropdownOpen);
  };

  const handleDirectTypeSelect = (directType: string) => {
    const currentPlatform = platformStates[active];
    if (currentPlatform) {
      const updatedPlatform = {
        ...currentPlatform,
        direct: {
          enabled: currentPlatform.direct?.enabled || false,
          keitaroData: currentPlatform.direct?.keitaroData || false,
          directType: directType as EDirectType
        }
      };

      const updatedPlatforms = {
        ...platformStates,
        [active]: updatedPlatform
      };

      setPlatformStates(updatedPlatforms);
      onPlatformDataChange(updatedPlatforms);
    }
    setIsDirectTypeDropdownOpen(false);
  };

  const getCurrentDirectType = (): string => {
    const currentPlatform = platformStates[active];
    return currentPlatform?.direct?.directType || 'keitaroOffer';
  };

  const getDirectTypeLabel = (directType: string): string => {
    const option = directTypeOptions.find(opt => opt.value === directType);
    return option ? option.label : '🎯 Keitaro Offer';
  };

  const updateTrackingParam = (paramName: string, value: string) => {
    if (!currentPlatform.direct?.keitaroData || typeof currentPlatform.direct.keitaroData === 'boolean') return;
    
    const updatedKeitaroData = {
      ...currentPlatform.direct.keitaroData,
      trackingParams: {
        naming: currentPlatform.direct.keitaroData.trackingParams?.naming || '',
        bundle_id: currentPlatform.direct.keitaroData.trackingParams?.bundle_id || '',
        advertising_id: currentPlatform.direct.keitaroData.trackingParams?.advertising_id || '',
        appsflyer_device_id: currentPlatform.direct.keitaroData.trackingParams?.appsflyer_device_id || '',
        [paramName]: value
      }
    };

    handleEditPlatformChange(active, {
      direct: {
        ...(currentPlatform.direct || {}),
        keitaroData: updatedKeitaroData
      }
    });
  };

  const copyToClipboard = async (text: string, fieldName: keyof typeof copyIconColors) => {
    try {
      await navigator.clipboard.writeText(text);
      // Змінюємо колір іконки на #34c3ff та знімаємо з інших полів
      setCopyIconColors(prev => {
        const newColors = { ...prev };
        // Скидаємо всі кольори до оригінального
        Object.keys(newColors).forEach(key => {
          newColors[key as keyof typeof copyIconColors] = '#6c757d';
        });
        // Встановлюємо активний колір для поточного поля
        newColors[fieldName] = '#34c3ff';
        return newColors;
      });

      // Змінюємо колір бордера Input на #34c3ff та знімаємо з інших полів
      setInputBorderColors(prev => {
        const newBorderColors = { ...prev };
        // Скидаємо всі кольори бордерів до оригінального
        Object.keys(newBorderColors).forEach(key => {
          newBorderColors[key as keyof typeof inputBorderColors] = '#34495e';
        });
        // Встановлюємо активний колір бордера для поточного поля
        newBorderColors[fieldName] = '#34c3ff';
        return newBorderColors;
      });
    } catch (err) {
      console.error('Помилка копіювання:', err);
    }
  };

  const handleToggleGroup = (group: 'adjust' | 'metrica' | 'appsflyer') => {
    setExpandedGroups((prevGroups) => ({
      ...prevGroups,
      [group]: !prevGroups[group],
    }));
  };

  const handleEditPlatformChange = (
    platformKey: string,
    updatedParams: Partial<IPlatformParams>
  ) => {
    console.log(
      "Editing platform:",
      platformKey,
      "with params:",
      updatedParams
    );
    setPlatformStates((prevStates) => {
      const updatedState = {
        ...prevStates,
        [platformKey]: {
          ...prevStates[platformKey],
          ...updatedParams,
        },
      };
      console.log("Updated platformStates:", updatedState);
      onPlatformDataChange(updatedState);
      return updatedState;
    });
  };

  const handleCreatePlatformChange = (
    index: number,
    updatedParams: Partial<IPlatformParams>
  ) => {
    console.log("Creating new platform with:", updatedParams);
    setNewPlatforms((prevPlatforms) => {
      const updatedPlatforms = [...prevPlatforms];
      updatedPlatforms[index] = {
        ...updatedPlatforms[index],
        ...updatedParams,
        enabled:
          updatedParams.enabled ?? updatedPlatforms[index]?.enabled ?? false,
        // geo field removed from IPlatformParams
        adjustParams: {
          ...updatedPlatforms[index]?.adjustParams,
          ...updatedParams.adjustParams,
          appId:
            updatedParams.adjustParams?.appId ??
            updatedPlatforms[index]?.adjustParams?.appId ??
            "",
          eventIds: {
            lead:
              updatedParams.adjustParams?.eventIds?.lead ??
              updatedPlatforms[index]?.adjustParams?.eventIds?.lead ??
              "",
            hold:
              updatedParams.adjustParams?.eventIds?.hold ??
              updatedPlatforms[index]?.adjustParams?.eventIds?.hold ??
              "",
            sale:
              updatedParams.adjustParams?.eventIds?.sale ??
              updatedPlatforms[index]?.adjustParams?.eventIds?.sale ??
              "",
            rejected:
              updatedParams.adjustParams?.eventIds?.rejected ??
              updatedPlatforms[index]?.adjustParams?.eventIds?.rejected ??
              "",
          },
        },
        metricaParams: {
          ...updatedPlatforms[index]?.metricaParams,
          ...updatedParams.metricaParams,
          appId:
            updatedParams.metricaParams?.appId ??
            updatedPlatforms[index]?.metricaParams?.appId ??
            0,
          postApiKey:
            updatedParams.metricaParams?.postApiKey ??
            updatedPlatforms[index]?.metricaParams?.postApiKey ??
            "",
        },
        // proxied field removed from IPlatformParams
        // keitaroData field removed - now only in direct.keitaroData
      };
      console.log("Updated newPlatforms:", updatedPlatforms);
      handleSaveNewPlatforms(updatedPlatforms);
      return updatedPlatforms;
    });
  };

  const handleAddNewPlatform = () => {
    const newPlatformData: IPlatformParams = {
      enabled: false,
      // geo, proxied, keitaroData fields removed from IPlatformParams
      adjustParams: {
        appId: "",
        eventIds: { lead: "", hold: "", sale: "", rejected: "" },
      },
      metricaParams: { appId: 0, postApiKey: "" },
    };

    // Update local state
    setPlatformStates((prevStates) => ({
      ...prevStates,
      [active]: {
        ...newPlatformData,
        newPlatform: true,
      },
    }));

    // Update parent's platforms object
    const filteredPlatforms: Record<string, IPlatformParams> = Object.fromEntries(
      Object.entries(platforms).filter(([_, value]) => value !== null) as [string, IPlatformParams][]
    );
    const updatedPlatforms: Record<string, IPlatformParams> = {
      ...filteredPlatforms,
      [active]: newPlatformData,
    };
    onPlatformDataChange(updatedPlatforms);
  };

  const handleSaveNewPlatforms = (
    platformsToSave: IPlatformParams[] = newPlatforms
  ) => {
    const updatedStates = { ...platformStates };
    platformsToSave.forEach((platform, index) => {
      const key = active;
      updatedStates[key] = platform;
    });
    setPlatformStates(updatedStates);
    setNewPlatforms([]);
    onPlatformDataChange(updatedStates);
    console.log("Created Platforms:", updatedStates);
  };

  // useEffect removed - keitaroData moved to direct.keitaroData
  // useEffect(() => {
  //   if (
  //     active === EPlatform.GENERAL &&
  //     typeof platformStates[EPlatform.GENERAL]?.keitaroData === "boolean" &&
  //     !platformStates[EPlatform.GENERAL]?.keitaroData
  //   ) {
  //     setPlatformStates((prevStates) => ({
  //       ...prevStates,
  //       [EPlatform.GENERAL]: {
  //         ...prevStates[EPlatform.GENERAL],
  //         keitaroData: true,
  //       },
  //     }));
  //   }
  // }, [platformStates, active]);

  const currentPlatform = platformStates[active] ||
    newPlatforms[0] || {
      adjustParams: {
        appId: "",
        eventIds: { lead: "", hold: "", sale: "", rejected: "" },
      },
      metricaParams: { appId: 0, postApiKey: "" },
      // geo, proxied, keitaroData fields removed from IPlatformParams
      enabled: false,
    };

  const availableCountries = Object.keys(countryNames).map((code) => ({
    label: getCountryLabel(code),
    value: code,
  }));

  // handleToggleChange removed - keitaroData moved to direct.keitaroData
  const handleToggleChange = (value: boolean) => {
    // Removed - no longer using top-level keitaroData
  };

  const handleConfirm = () => {
    // Removed - no longer using top-level keitaroData
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  console.log(platformStates)

  return (
    <>
      {platformStates && (
        <div className="platforms-accordion">
          <div className="platforms-layout">
            {/* Platforms sidebar removed - simplified view */}

            {/* Platform settings */}
            <div className="platforms-content" style={{ width: '100%', maxWidth: '100%' }}>
              <div className="platform-content">
                {active && platforms && platforms[active] ? (
                  <div className="platform-details">
                    <div className="platform-params">
                      {/* Banner params Title */}
                      {
                        app.bannerParams?.dataPath && (
                          <>
                            <div className="field-group banner-title-group" style={{ padding: 0, margin: '8px 0 8px 0' }}>
                              <div className="group-header" style={{ padding: 0, display: 'inline-flex', alignItems: 'center' }}>
                                <div className="group-line" style={{ backgroundColor: '#3498db' }}></div>
                                <h3 
                                  style={{ textTransform: 'none', color: '#ffffff', margin: 0, fontSize: '16px', cursor: 'pointer' }}
                                  onClick={() => {
                                    const url = `https://${app.domainParams.name}/${app.bannerParams?.dataPath}`;
                                    const params = [
                                      `**URL** – ${url}`,
                                      '',
                                      `**dataPath** – \`${app.bannerParams?.dataPath || ''}\``,
                                      `**imagePath** – \`${app.bannerParams?.imagePath || ''}\``,
                                      `**linkParam** – \`${app.bannerParams?.linkParam || ''}\``,
                                      `**imageParam** – \`${app.bannerParams?.imageParam || ''}\``
                                    ].join('\n');
                                    navigator.clipboard.writeText(params);
                                  }}
                                  title="Копіювати всі параметри (Telegram формат)"
                                >
                                  Налаштування ендпоінта
                                </h3>
                                <FontAwesomeIcon 
                                  icon={faCopy} 
                                  style={{ color: '#3498db', fontSize: '12px', cursor: 'pointer', marginLeft: '8px' }}
                                  onClick={() => {
                                    const url = `https://${app.domainParams.name}/${app.bannerParams?.dataPath}`;
                                    const params = [
                                      `**URL** – ${url}`,
                                      '',
                                      `**dataPath** – \`${app.bannerParams?.dataPath || ''}\``,
                                      `**imagePath** – \`${app.bannerParams?.imagePath || ''}\``,
                                      `**linkParam** – \`${app.bannerParams?.linkParam || ''}\``,
                                      `**imageParam** – \`${app.bannerParams?.imageParam || ''}\``
                                    ].join('\n');
                                    navigator.clipboard.writeText(params);
                                  }}
                                  title="Копіювати всі параметри (Telegram формат)"
                                />
                              </div>
                            </div>
                            
                            {/* Banner URL */}
                            <div style={{ 
                              marginBottom: '16px',
                              padding: '10px 14px',
                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: '6px',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              const url = `https://${app.domainParams.name}/${app.bannerParams?.dataPath}`;
                              navigator.clipboard.writeText(url);
                            }}>
                              <div style={{ flex: 1, overflow: 'hidden' }}>
                                <div style={{ fontSize: '11px', color: '#a4a9b3', marginBottom: '4px', textTransform: 'uppercase' }}>URL</div>
                                <a 
                                  href={`https://${app.domainParams.name}/${app.bannerParams?.dataPath}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    color: '#3498db',
                                    textDecoration: 'underline',
                                    fontFamily: 'monospace',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {`https://${app.domainParams.name}/${app.bannerParams?.dataPath}`}
                                </a>
                              </div>
                            </div>

                            {/* Banner Params Display */}
                            <div style={{ 
                              display: 'grid', 
                              gridTemplateColumns: 'repeat(2, 1fr)', 
                              gap: '8px',
                              marginBottom: '12px'
                            }}>
                              {/* dataPath */}
                              <div style={{
                                padding: '8px 12px',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '6px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer'
                              }}
                              onClick={() => navigator.clipboard.writeText(app.bannerParams?.dataPath || '')}>
                                <div>
                                  <div style={{ fontSize: '11px', color: '#a4a9b3', marginBottom: '2px' }}>dataPath</div>
                                  <div style={{ fontSize: '14px', fontFamily: 'monospace', color: '#ffffff' }}>{app.bannerParams?.dataPath}</div>
                          </div>
                                <FontAwesomeIcon icon={faCopy} style={{ color: '#ffffff', fontSize: '12px' }} />
                        </div>

                              {/* imagePath */}
                              <div style={{
                                padding: '8px 12px',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '6px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer'
                              }}
                              onClick={() => navigator.clipboard.writeText(app.bannerParams?.imagePath || '')}>
                                <div>
                                  <div style={{ fontSize: '11px', color: '#a4a9b3', marginBottom: '2px' }}>imagePath</div>
                                  <div style={{ fontSize: '14px', fontFamily: 'monospace', color: '#ffffff' }}>{app.bannerParams?.imagePath}</div>
                            </div>
                                <FontAwesomeIcon icon={faCopy} style={{ color: '#ffffff', fontSize: '12px' }} />
                          </div>
                          
                              {/* linkParam */}
                              <div style={{
                                padding: '8px 12px',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '6px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer'
                              }}
                              onClick={() => navigator.clipboard.writeText(app.bannerParams?.linkParam || '')}>
                                <div>
                                  <div style={{ fontSize: '11px', color: '#a4a9b3', marginBottom: '2px' }}>linkParam</div>
                                  <div style={{ fontSize: '14px', fontFamily: 'monospace', color: '#ffffff' }}>{app.bannerParams?.linkParam}</div>
                              </div>
                                <FontAwesomeIcon icon={faCopy} style={{ color: '#ffffff', fontSize: '12px' }} />
                            </div>

                              {/* imageParam */}
                              <div style={{
                                padding: '8px 12px',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                borderRadius: '6px',
                                border: '1px solid rgba(255, 255, 255, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer'
                              }}
                              onClick={() => navigator.clipboard.writeText(app.bannerParams?.imageParam || '')}>
                                <div>
                                  <div style={{ fontSize: '11px', color: '#a4a9b3', marginBottom: '2px' }}>imageParam</div>
                                  <div style={{ fontSize: '14px', fontFamily: 'monospace', color: '#ffffff' }}>{app.bannerParams?.imageParam}</div>
                          </div>
                                <FontAwesomeIcon icon={faCopy} style={{ color: '#ffffff', fontSize: '12px' }} />
                        </div>
                          </div>
                          </>
                        )
                      }

                      {/* Adjust section hidden */}
                      <div className="field-group adjust-group" style={{ display: 'none' }}>
                        <div className="group-header">
                          <div className="group-line"></div>
                          <h3>Adjust</h3>
                        </div>
                        <div className={`group-fields ${expandedGroups.adjust ? 'expanded' : ''}`}>
                          <div className="input-field">
                            <label>App ID</label>
                            <Input
                              value={currentPlatform.adjustParams?.appId || ""}
                              onChange={(value) =>
                                handleEditPlatformChange(active, {
                                  adjustParams: {
                                    ...currentPlatform.adjustParams!,
                                    appId: value,
                                  },
                                })
                              }
                            />
                          </div>
                          <div className="input-field">
                            <label>Lead Event ID</label>
                            <Input
                              value={currentPlatform.adjustParams?.eventIds.lead || ""}
                              onChange={(value) =>
                                handleEditPlatformChange(active, {
                                  adjustParams: {
                                    ...currentPlatform.adjustParams!,
                                    eventIds: {
                                      ...currentPlatform.adjustParams!.eventIds,
                                      lead: value,
                                    },
                                  },
                                })
                              }
                            />
                          </div>
                          <div className="input-field">
                            <label>Hold Event ID</label>
                            <Input
                              value={currentPlatform.adjustParams?.eventIds.hold || ""}
                              onChange={(value) =>
                                handleEditPlatformChange(active, {
                                  adjustParams: {
                                    ...currentPlatform.adjustParams!,
                                    eventIds: {
                                      ...currentPlatform.adjustParams!.eventIds,
                                      hold: value,
                                    },
                                  },
                                })
                              }
                            />
                          </div>
                          <div className="input-field">
                            <label>Sale Event ID</label>
                            <Input
                              value={currentPlatform.adjustParams?.eventIds.sale || ""}
                              onChange={(value) =>
                                handleEditPlatformChange(active, {
                                  adjustParams: {
                                    ...currentPlatform.adjustParams!,
                                    eventIds: {
                                      ...currentPlatform.adjustParams!.eventIds,
                                      sale: value,
                                    },
                                  },
                                })
                              }
                            />
                          </div>
                          <div className="input-field">
                            <label>Rejected Event ID</label>
                            <Input
                              value={currentPlatform.adjustParams?.eventIds.rejected || ""}
                              onChange={(value) =>
                                handleEditPlatformChange(active, {
                                  adjustParams: {
                                    ...currentPlatform.adjustParams!,
                                    eventIds: {
                                      ...currentPlatform.adjustParams!.eventIds,
                                      rejected: value,
                                    },
                                  },
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* AppMetrica section hidden */}
                      <div className="field-group metrica-group" style={{ display: 'none' }}>
                        <div className="group-header">
                          <div className="group-line"></div>
                          <h3>AppMetrica</h3>
                        </div>
                        <div className={`group-fields ${expandedGroups.metrica ? 'expanded' : ''}`}>
                          <div className="input-field">
                            <label>App ID</label>
                            <Input
                              value={currentPlatform.metricaParams?.appId?.toString() || ""}
                              onChange={(value) =>
                                handleEditPlatformChange(active, {
                                  metricaParams: {
                                    ...currentPlatform.metricaParams!,
                                    appId: parseInt(value) || 0,
                                  },
                                })
                              }
                            />
                          </div>
                          <div className="input-field">
                            <label>Post API Key</label>
                            <Input
                              value={currentPlatform.metricaParams?.postApiKey || ""}
                              onChange={(value) =>
                                handleEditPlatformChange(active, {
                                  metricaParams: {
                                    ...currentPlatform.metricaParams!,
                                    postApiKey: value,
                                  },
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>

                      {/* AppsFlyer section - always expanded, uncollapsable */}
                      <div className="field-group appsflyer-group" style={{ paddingRight: 0, marginTop: '16px' }}>
                        <div className="group-header" style={{ paddingRight: 0, display: 'flex', alignItems: 'center' }}>
                          <div className="group-line"></div>
                          <h3 style={{ textTransform: 'none', margin: 0, fontSize: '16px' }}>AppsFlyer</h3>
                          </div>
                        <div className="group-fields expanded" style={{ paddingRight: 0 }}>
                          <div className="input-field" style={{ paddingRight: 0 }}>
                            <label>API Token</label>
                            <Input
                              value={currentPlatform.appsflyerParams?.apiToken || ""}
                              onChange={(value) =>
                                handleEditPlatformChange(active, {
                                  appsflyerParams: {
                                    ...currentPlatform.appsflyerParams!,
                                    apiToken: value,
                                  },
                                })
                              }
                            />
                          </div>
                          <div className="input-field" style={{ paddingRight: 0 }}>
                            <label>Dev Key</label>
                            <Input
                              value={currentPlatform.appsflyerParams?.devKey || ""}
                              onChange={(value) =>
                                handleEditPlatformChange(active, {
                                  appsflyerParams: {
                                    ...currentPlatform.appsflyerParams!,
                                    devKey: value,
                                  },
                                })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="add-platform-section">
                    <button 
                      className="add-platform-btn"
                      onClick={() => handleAddNewPlatform()}
                    >
                      Додати нову платформу
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Modal
            open={isModalOpen}
            onClose={handleCancel}
            className="custom_modal_aprove"
          >
            <Modal.Body>Створити нову компанію?</Modal.Body>
            <Modal.Footer>
              <Button onClick={handleConfirm} appearance="primary">
                Підтвердити
              </Button>
              <Button onClick={handleCancel} appearance="subtle">
                Відхилити
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}

      {/* Direct type dropdown positioned outside content */}
      {isDirectTypeDropdownOpen && directTypeDropdownPosition && (
        <div
          ref={directTypeDropdownRef}
          className="direct-type-dropdown"
          style={{
            position: 'fixed',
            left: directTypeDropdownPosition.x,
            top: directTypeDropdownPosition.y,
            zIndex: 9999,
            backgroundColor: '#2c3e50',
            border: '1px solid #34495e',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            minWidth: '250px',
            maxHeight: '200px',
            overflowY: 'auto'
          }}
        >
          {directTypeOptions.map((option) => (
            <div
              key={option.value}
              onClick={() => handleDirectTypeSelect(option.value)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 16px',
                cursor: 'pointer',
                backgroundColor: getCurrentDirectType() === option.value ? '#34495e' : 'transparent',
                transition: 'background-color 0.2s ease',
                borderBottom: '1px solid #34495e'
              }}
              onMouseEnter={(e) => {
                if (getCurrentDirectType() !== option.value) {
                  e.currentTarget.style.backgroundColor = '#34495e';
                }
              }}
              onMouseLeave={(e) => {
                if (getCurrentDirectType() !== option.value) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <span style={{ fontSize: '14px', color: '#ecf0f1' }}>
                {option.label}
              </span>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default PlatformsAccordion;



